"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import Script from "next/script";
import cn from "classnames";
import { useTheme } from "next-themes";

import type { LayoutType } from "../../../../../utils/layout";

import styles from "./Tweet.module.scss";
import { getTwitterWidget, twitterScript } from "../utils";
import { TweetSkeleton } from "../../../../skeletons/TweetSkeleton/TweetSkeleton";

const twitterWidgetInit = () => (
  <Script
    id="twitter-script"
    type="application/javascript"
    strategy="afterInteractive"
    dangerouslySetInnerHTML={{
      __html: twitterScript,
    }}
  />
);

export { twitterWidgetInit };

const EmbeddedTweet = ({
  tweetId,
  onLoad,
  isLoading,
}: {
  tweetId: string;
  onLoad: () => void;
  isLoading: boolean;
}) => {
  const tweetRef = useRef<HTMLDivElement | null>(null);
  const { theme } = useTheme();

  const loadTweet = useCallback(async () => {
    try {
      const twitterWidget = await getTwitterWidget();
      if (tweetRef.current && twitterWidget) {
        await twitterWidget?.widgets?.createTweet(tweetId, tweetRef.current, {
          theme,
        });
      }
    } catch (e) {
      console.error(e);
    }
  }, [tweetRef, tweetId, theme]);

  useEffect(() => {
    if (isLoading) {
      onLoad();
      loadTweet();
    }
  }, [loadTweet, tweetId, isLoading, onLoad]);

  return (
    <div
      data-testid="embeddedTweet"
      ref={tweetRef}
      className={styles.embeddedTweet}
    />
  );
};

const TweetResource = ({
  tweetId,
  layoutType,
}: {
  tweetId: string;
  layoutType?: LayoutType;
}) => {
  const [isLoading, setIsLoading] = useState(true);
  return (
    <div
      className={cn([styles.tweetContainer, layoutType && styles[layoutType]])}
    >
      {isLoading && <TweetSkeleton />}
      <div className={styles.tweet}>
        <Script
          id="twitter-script"
          type="application/javascript"
          strategy="afterInteractive"
          dangerouslySetInnerHTML={{
            __html: twitterScript,
          }}
        />
        <EmbeddedTweet
          tweetId={tweetId}
          isLoading={isLoading}
          onLoad={() => setIsLoading(false)}
        />
      </div>
    </div>
  );
};

export { TweetResource };
